import { useState, useEffect } from 'react';
import { Container } from '@/components/layout/Container';
import { Stack } from '@/components/layout/Stack';
import { useNavigationStore } from '@/store/navigationStore';
import { NotificationSettings } from '@/components/notifications/NotificationSettings';
import { NotificationHistory } from '@/components/notifications/NotificationHistory';
import { BrowserPermissionRequest } from '@/components/notifications/BrowserPermissionRequest';
import { MockNotificationDisplay } from '@/components/notifications/MockNotificationDisplay';
import { smartNotificationService } from '@/services/intelligentScheduling/SmartNotificationService';
import { useListsStore } from '@/store/listsStore';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Bell, History as HistoryIcon, Settings, TestTube } from 'lucide-react';

export function Notifications() {
  const { setPageInfo } = useNavigationStore();
  const { lists, initializeLists } = useListsStore();
  const [activeTab, setActiveTab] = useState('settings');
  const [hasPermission, setHasPermission] = useState<NotificationPermission>('default');
  const [nextNotification, setNextNotification] = useState<any>(null);

  useEffect(() => {
    setPageInfo('Notifications');
    checkNotificationPermission();
    initializeLists();
  }, [setPageInfo, initializeLists]);

  // Load next scheduled notification using smart logic
  useEffect(() => {
    const loadNextNotification = async () => {
      try {
        const next = await smartNotificationService.getNextScheduledNotification();
        setNextNotification(next);
      } catch (error) {
        console.error('Failed to load next notification:', error);
      }
    };

    loadNextNotification();
    
    // Refresh when lists change
    const interval = setInterval(loadNextNotification, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [lists]);

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setHasPermission(Notification.permission);
    }
  };

  return (
    <Container size="lg" className="py-6">
      <Stack spacing="lg">
        {/* Permission Request */}
        {hasPermission !== 'granted' && (
          <BrowserPermissionRequest 
            permission={hasPermission}
            onPermissionChange={setHasPermission}
          />
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="settings" className="flex items-center">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center">
              <HistoryIcon className="h-4 w-4 mr-2" />
              History
            </TabsTrigger>
            <TabsTrigger value="test" className="flex items-center">
              <TestTube className="h-4 w-4 mr-2" />
              Test
            </TabsTrigger>
            <TabsTrigger value="preview" className="flex items-center">
              <Bell className="h-4 w-4 mr-2" />
              Preview
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="mt-6">
            <NotificationSettings hasPermission={hasPermission === 'granted'} />
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            <NotificationHistory />
          </TabsContent>

          <TabsContent value="test" className="mt-6">
            <MockNotificationDisplay />
          </TabsContent>

          <TabsContent value="preview" className="mt-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Live Notification Preview</h3>
                <p className="text-muted-foreground mb-4">
                  See how your current notification settings will look in real-time.
                </p>
              </div>

              {/* Preview Card */}
              <div className="p-4 rounded-lg border bg-card">
                <h4 className="text-sm font-medium mb-3 text-muted-foreground">Next Notification Preview:</h4>
                {nextNotification ? (
                  <div className="space-y-2">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/20">
                        <Bell className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{nextNotification.notification.title}</h3>
                        <p className="text-muted-foreground mt-1">
                          {nextNotification.notification.message}
                          {nextNotification.scheduleInfo.nextItem?.description && (
                            <span className="text-sm block mt-1 italic">
                              {nextNotification.scheduleInfo.nextItem.description}
                            </span>
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            nextNotification.notification.priority === 'high' 
                              ? 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400'
                              : 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                          }`}>
                            {nextNotification.notification.priority === 'high' ? 'High Priority' : 'Medium Priority'}
                          </span>
                          <span className="px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs font-medium">
                            {Math.round((nextNotification.scheduleInfo.confidence || 0.85) * 100)}% Confidence
                          </span>
                        </div>
                        {nextNotification.scheduleInfo.reasoning && nextNotification.scheduleInfo.reasoning.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-muted-foreground mb-1">Why this task?</p>
                            <div className="flex flex-wrap gap-1">
                              {nextNotification.scheduleInfo.reasoning.map((reason: string, index: number) => (
                                <span key={index} className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs">
                                  {reason}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
                        <Bell className="h-4 w-4 text-gray-500" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">No scheduled notifications</h3>
                        <p className="text-muted-foreground mt-1">
                          All your tasks are completed or no active schedules found.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <Button 
                onClick={async () => {
                  console.log('Sending smart test notification...');
                  
                  try {
                    // Use smart logic to get the next notification
                    const nextNotificationData = await smartNotificationService.getNextScheduledNotification();
                    
                    if (nextNotificationData) {
                      const { notification, scheduleInfo } = nextNotificationData;
                      
                      console.log('Smart notification found:', {
                        listTitle: scheduleInfo.listTitle,
                        nextItem: scheduleInfo.nextItem?.content,
                        confidence: scheduleInfo.confidence,
                        reasoning: scheduleInfo.reasoning
                      });
                      
                      // Send the intelligent notification
                      await smartNotificationService.sendScheduleNotification(
                        `schedule_${scheduleInfo.listId}_${Date.now()}`,
                        scheduleInfo.listId,
                        notification.title || 'Smart Notification',
                        notification.message || 'Time to focus on your next task!',
                        'schedule_reminder',
                        {
                          priority: notification.priority as 'low' | 'medium' | 'high' | 'critical' || 'medium',
                          confidence: scheduleInfo.confidence,
                          contextFactors: [...(notification.contextualFactors || []), 'test_notification']
                        }
                      );
                    } else {
                      console.log('No smart notification available, sending fallback');
                      // Fallback notification
                      await smartNotificationService.sendScheduleNotification(
                        'fallback-schedule-1',
                        'fallback-list-1',
                        '🎯 All caught up!',
                        'Great job! All your scheduled tasks are completed or no active schedules found.',
                        'schedule_reminder',
                        {
                          priority: 'medium',
                          confidence: 0.85,
                          contextFactors: ['all_tasks_complete', 'test_notification']
                        }
                      );
                    }
                  } catch (error) {
                    console.error('Failed to send smart test notification:', error);
                  }
                }}
                className="w-full"
              >
                <TestTube className="h-4 w-4 mr-2" />
                Send Test Notification
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </Stack>
    </Container>
  );
}